import type { Candle, CandleInterval } from '../types';
import type { ColorProTradingSuiteV31OverlayResult } from './colorProTradingSuiteV31';
import { roundTripFeePercent } from '../strategies/common/fees';

export interface ColorProTradingSuiteV31StrategyInputs {
  timeframe: CandleInterval;
  useOneMinuteStopRefinement: boolean;
  skipStepOnRedClose: boolean;
  requireMa200AboveLastCandle: boolean;
  stopBufferPercent: number;
  makerFeePercent: number;
  takerFeePercent: number;
  runnerTrailPercent: number;
  maxHistoryCandles: number;
}

export interface ColorProTradingSuiteV31StrategyTrade {
  entryIndex: number;
  entryPrice: number;
  entryTime: string | null;
  yellowLow: number;
  stopPrice: number;
  exitIndex: number;
  exitPrice: number;
  exitTime: string | null;
  durationCandles: number;
  executionResolution: '1m-refined' | 'timeframe-fallback';
  exitReason: 'trailingStop';
  pnlPercent: number;
}

export interface ColorProTradingSuiteV31StrategyMarker {
  index: number;
  type: 'buy' | 'sell';
  reason?: 'trailingStop';
}

export interface ColorProTradingSuiteV31StrategyStats {
  totalTrades: number;
  trailingStopCount: number;
  tradesPerDay: number;
  winRate: number;
  totalPnlPercent: number;
  averagePnlPercent: number;
}

export interface ColorProTradingSuiteV31StrategyActiveTrade {
  state: 'inTrade';
  entryIndex: number;
  entryPrice: number;
  yellowLow: number;
  stopPrice: number;
}

export interface ColorProTradingSuiteV31StrategyOverlayResult {
  markers: ColorProTradingSuiteV31StrategyMarker[];
  trades: ColorProTradingSuiteV31StrategyTrade[];
  stats: ColorProTradingSuiteV31StrategyStats;
  activeTrade: ColorProTradingSuiteV31StrategyActiveTrade | null;
}

interface ActiveTrade {
  entryIndex: number;
  entryPrice: number;
  yellowLow: number;
  initialStopPrice: number;
  stopPrice: number;
}

type MinuteCoverageByTimeframeCandle = Map<number, boolean> | Record<string, boolean>;

function emptyStats(): ColorProTradingSuiteV31StrategyStats {
  return {
    totalTrades: 0,
    trailingStopCount: 0,
    tradesPerDay: 0,
    winRate: 0,
    totalPnlPercent: 0,
    averagePnlPercent: 0,
  };
}

function clampPositive(value: number, fallback: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return value;
}

function timeframeToMilliseconds(interval: CandleInterval): number {
  if (interval === '1m') {
    return 60_000;
  }
  if (interval === '3m') {
    return 3 * 60_000;
  }
  if (interval === '5m') {
    return 5 * 60_000;
  }
  if (interval === '15m') {
    return 15 * 60_000;
  }
  if (interval === '30m') {
    return 30 * 60_000;
  }
  if (interval === '45m') {
    return 45 * 60_000;
  }
  if (interval === '1h') {
    return 60 * 60_000;
  }
  if (interval === '2h') {
    return 2 * 60 * 60_000;
  }
  if (interval === '3h') {
    return 3 * 60 * 60_000;
  }
  if (interval === '4h') {
    return 4 * 60 * 60_000;
  }
  if (interval === '1d') {
    return 24 * 60 * 60_000;
  }
  if (interval === '1w') {
    return 7 * 24 * 60 * 60_000;
  }
  return 30 * 24 * 60 * 60_000;
}

function toCandleTimeMs(candle: Candle): number {
  return Date.parse(candle.time);
}

function bucketStartForInterval(timeMs: number, intervalMs: number): number {
  return Math.floor(timeMs / intervalMs) * intervalMs;
}

function buildMinuteChildMap(minuteCandles: Candle[], timeframeMs: number): Map<number, Candle[]> {
  const grouped = new Map<number, Candle[]>();

  for (const candle of minuteCandles) {
    const timeMs = toCandleTimeMs(candle);
    if (!Number.isFinite(timeMs)) {
      continue;
    }

    const bucketStart = bucketStartForInterval(timeMs, timeframeMs);
    const current = grouped.get(bucketStart);
    if (current) {
      current.push(candle);
    } else {
      grouped.set(bucketStart, [candle]);
    }
  }

  for (const [, bucket] of grouped) {
    bucket.sort((left, right) => toCandleTimeMs(left) - toCandleTimeMs(right));
  }

  return grouped;
}

function longStopTouched(candle: Candle, stopPrice: number): boolean {
  if (candle.open <= stopPrice) {
    return true;
  }
  return candle.low <= stopPrice && stopPrice <= candle.high;
}

function resolveLongStopExitPrice(stopPrice: number, candleOpen: number): number {
  if (Number.isFinite(candleOpen) && candleOpen <= stopPrice) {
    return candleOpen;
  }
  return stopPrice;
}

function hasMinuteCoverageForBucket(
  coverageByBucket: MinuteCoverageByTimeframeCandle | undefined,
  bucketStart: number,
): boolean | undefined {
  if (!coverageByBucket) {
    return undefined;
  }

  if (coverageByBucket instanceof Map) {
    return coverageByBucket.get(bucketStart);
  }

  const key = String(bucketStart);
  const coverage = coverageByBucket[key];
  if (typeof coverage === 'boolean') {
    return coverage;
  }
  return undefined;
}

function daysCovered(candles: Candle[]): number {
  if (candles.length < 2) {
    return 1;
  }

  const first = Date.parse(candles[0].time);
  const last = Date.parse(candles[candles.length - 1].time);
  if (!Number.isFinite(first) || !Number.isFinite(last) || last <= first) {
    return 1;
  }

  return Math.max(1, (last - first) / 86_400_000);
}

function smaSeriesFromCandles(candles: Candle[], length: number): (number | undefined)[] {
  const output = new Array<number | undefined>(candles.length).fill(undefined);
  if (length <= 0 || candles.length === 0) {
    return output;
  }

  let rollingSum = 0;
  for (let index = 0; index < candles.length; index += 1) {
    rollingSum += candles[index].close;
    if (index >= length) {
      rollingSum -= candles[index - length].close;
    }
    if (index >= length - 1) {
      output[index] = rollingSum / length;
    }
  }
  return output;
}

function buildStats(
  candles: Candle[],
  trades: ColorProTradingSuiteV31StrategyTrade[],
): ColorProTradingSuiteV31StrategyStats {
  if (trades.length === 0) {
    return emptyStats();
  }

  const trailingStopCount = trades.filter((trade) => trade.exitReason === 'trailingStop').length;
  const wins = trades.filter((trade) => trade.pnlPercent >= 0).length;
  const totalPnlPercent = trades.reduce((sum, trade) => sum + trade.pnlPercent, 0);

  return {
    totalTrades: trades.length,
    trailingStopCount,
    tradesPerDay: trades.length / daysCovered(candles),
    winRate: (wins / trades.length) * 100,
    totalPnlPercent,
    averagePnlPercent: totalPnlPercent / trades.length,
  };
}

export function computeColorProTradingSuiteV31StrategyOverlay(
  candles: Candle[],
  colorProOverlay: ColorProTradingSuiteV31OverlayResult | null,
  inputs: ColorProTradingSuiteV31StrategyInputs,
  minuteCandles?: Candle[],
  minuteCoverageByTimeframeCandle?: MinuteCoverageByTimeframeCandle,
): ColorProTradingSuiteV31StrategyOverlayResult {
  if (!colorProOverlay || candles.length === 0) {
    return {
      markers: [],
      trades: [],
      stats: emptyStats(),
      activeTrade: null,
    };
  }

  const stopBufferPercent = Math.max(0, Number.isFinite(inputs.stopBufferPercent) ? inputs.stopBufferPercent : 0);
  const runnerTrailPercent = Number.isFinite(inputs.runnerTrailPercent)
    ? Math.min(100, Math.max(0, inputs.runnerTrailPercent))
    : 1.2;
  const combinedFeePercent = roundTripFeePercent(
    clampPositive(inputs.makerFeePercent, 0),
    clampPositive(inputs.takerFeePercent, 0),
  );
  const ma200 = smaSeriesFromCandles(candles, 200);
  const timeframeMs = timeframeToMilliseconds(inputs.timeframe);
  const requiredMinuteChildren = Math.max(1, Math.round(timeframeMs / 60_000));
  const minuteChildrenByStrategyBucketStart =
    inputs.useOneMinuteStopRefinement && Array.isArray(minuteCandles) && minuteCandles.length > 0
      ? buildMinuteChildMap(minuteCandles, timeframeMs)
      : null;

  const markers: ColorProTradingSuiteV31StrategyMarker[] = [];
  const trades: ColorProTradingSuiteV31StrategyTrade[] = [];

  let activeTrade: ActiveTrade | null = null;

  for (let index = 0; index < candles.length; index += 1) {
    const candle = candles[index];
    const candleState = colorProOverlay.candleStates[index];

    if (!activeTrade) {
      if (candleState !== 'bullishUnconfirmedReversal') {
        continue;
      }

      if (inputs.requireMa200AboveLastCandle) {
        const currentMa200 = ma200[index];
        const previousMa200 = index > 0 ? ma200[index - 1] : undefined;
        if (
          currentMa200 === undefined ||
          previousMa200 === undefined ||
          !(currentMa200 > previousMa200)
        ) {
          continue;
        }
      }

      const entryPrice = candle.close;
      const yellowLow = candle.low;
      const stopPrice = yellowLow * (1 - stopBufferPercent / 100);

      if (entryPrice <= 0 || stopPrice <= 0) {
        continue;
      }

      activeTrade = {
        entryIndex: index,
        entryPrice,
        yellowLow,
        initialStopPrice: stopPrice,
        stopPrice,
      };
      markers.push({ index, type: 'buy' });
      continue;
    }

    const shouldStepThisCandle = !(inputs.skipStepOnRedClose && candle.close < candle.open);
    if (shouldStepThisCandle) {
      const steppedStopPrice = activeTrade.stopPrice * (1 + runnerTrailPercent / 100);
      if (steppedStopPrice > candle.high) {
        activeTrade.stopPrice = candle.high;
      } else {
        activeTrade.stopPrice = steppedStopPrice;
      }
    }

    let stopHit = false;
    let exitPrice = activeTrade.stopPrice;
    let exitTime = candle.time;
    let executionResolution: '1m-refined' | 'timeframe-fallback' = 'timeframe-fallback';

    const candleTimeMs = toCandleTimeMs(candle);
    const bucketStart = bucketStartForInterval(candleTimeMs, timeframeMs);
    const oneMinuteChildren = Number.isFinite(bucketStart)
      ? minuteChildrenByStrategyBucketStart?.get(bucketStart)
      : undefined;
    const explicitCoverage = Number.isFinite(bucketStart)
      ? hasMinuteCoverageForBucket(minuteCoverageByTimeframeCandle, bucketStart)
      : undefined;
    const hasMinuteCoverage =
      explicitCoverage !== undefined
        ? explicitCoverage
        : Boolean(oneMinuteChildren && oneMinuteChildren.length >= requiredMinuteChildren);

    if (inputs.useOneMinuteStopRefinement && hasMinuteCoverage && oneMinuteChildren) {
      for (const oneMinuteCandle of oneMinuteChildren) {
        if (!longStopTouched(oneMinuteCandle, activeTrade.stopPrice)) {
          continue;
        }

        stopHit = true;
        executionResolution = '1m-refined';
        exitPrice = resolveLongStopExitPrice(activeTrade.stopPrice, oneMinuteCandle.open);
        exitTime = oneMinuteCandle.time;
        break;
      }
    }

    if (!stopHit) {
      stopHit = candle.low <= activeTrade.stopPrice && activeTrade.stopPrice <= candle.high;
      executionResolution = 'timeframe-fallback';
      exitPrice = activeTrade.stopPrice;
      exitTime = candle.time;
    }

    if (!stopHit) {
      continue;
    }

    const exitReason: 'trailingStop' = 'trailingStop';

    const grossPnlPercent = ((exitPrice - activeTrade.entryPrice) / activeTrade.entryPrice) * 100;
    const pnlPercent = grossPnlPercent - combinedFeePercent;

    trades.push({
      entryIndex: activeTrade.entryIndex,
      entryPrice: activeTrade.entryPrice,
      entryTime: candles[activeTrade.entryIndex]?.time ?? null,
      yellowLow: activeTrade.yellowLow,
      stopPrice: activeTrade.initialStopPrice,
      exitIndex: index,
      exitPrice,
      exitTime,
      durationCandles: Math.max(1, index - activeTrade.entryIndex),
      executionResolution,
      exitReason,
      pnlPercent,
    });

    markers.push({ index, type: 'sell', reason: exitReason });
    activeTrade = null;
  }

  return {
    markers,
    trades,
    stats: buildStats(candles, trades),
    activeTrade: activeTrade
      ? {
          state: 'inTrade',
          entryIndex: activeTrade.entryIndex,
          entryPrice: activeTrade.entryPrice,
          yellowLow: activeTrade.yellowLow,
          stopPrice: activeTrade.stopPrice,
        }
      : null,
  };
}
